/**
 * MANAGEMENT (14) formatting + tone helpers. Dates render locale-aware (FR/AR) with the numeric face kept
 * LTR. Tone mappers live here (not in a screen) so the Overview tiles and the section screens share them
 * without tripping the react-refresh single-export rule.
 */
import type { Locale } from "@/app";
import type { BadgeTone } from "@/ui";
import type { LabLifecycleStatut, SubscriptionStatut, TicketStatut } from "./types";

/** Map our app Locale to a BCP-47 tag for Intl. AR uses the Algerian Arabic locale. */
function bcp47(locale: Locale): string {
  return locale === "ar" ? "ar-DZ" : "fr-DZ";
}

/** ISO timestamp → localized date+time, e.g. "29/06/2026 14:32". Returns "—" on a missing value. */
export function formatDateTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp47(locale), { dateStyle: "short", timeStyle: "short" }).format(d);
}

/** ISO timestamp → localized date only, e.g. "29/06/2026". */
export function formatDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp47(locale), { dateStyle: "medium" }).format(d);
}

/**
 * Badge tone per central subscription statut (mirrors the lab cockpit's statutTone so the two consoles
 * agree): ACTIVE → success · ESSAI → info · PAST_DUE → warn · SUSPENDUE/EXPIREE/ANNULEE → danger.
 */
export function subscriptionTone(statut: SubscriptionStatut | null | undefined): BadgeTone {
  switch (statut) {
    case "ACTIVE":
      return "success";
    case "ESSAI":
      return "info";
    case "PAST_DUE":
      return "warn";
    case "SUSPENDUE":
    case "EXPIREE":
    case "ANNULEE":
      return "danger";
    default:
      return "neutral";
  }
}

/** Badge tone per lab lifecycle statut (actif → success · suspendu → danger). */
export function labStatutTone(statut: LabLifecycleStatut | null | undefined): BadgeTone {
  return statut === "actif" ? "success" : statut === "suspendu" ? "danger" : "neutral";
}

/**
 * Badge tone per lab-access-request statut — the intake funnel lifecycle. New work catches the eye (info),
 * triage is in flight (warn), a decision settles it (approuvee → success · rejetee → danger). Paired with a
 * localized label + a glyph everywhere it renders — never color-only (daltonisme + médico-légal).
 */
export function demandeStatutTone(
  statut: "demande" | "en_revue" | "approuvee" | "rejetee" | null | undefined,
): BadgeTone {
  switch (statut) {
    case "demande":
      return "info";
    case "en_revue":
      return "warn";
    case "approuvee":
      return "success";
    case "rejetee":
      return "danger";
    default:
      return "neutral";
  }
}

/** Badge tone per support-ticket statut (open work warns; resolved/closed settle). */
export function ticketStatutTone(statut: TicketStatut | null | undefined): BadgeTone {
  switch (statut) {
    case "ouvert":
      return "warn";
    case "en_cours":
      return "info";
    case "resolu":
      return "success";
    case "ferme":
      return "neutral";
    default:
      return "neutral";
  }
}
